import type { ApiResult } from '@van-sales/shared'
import { idb, STORES, deductInventoryForSale, addInventoryForPurchase, restoreInventoryForFailedSale, restoreInventoryForFailedPurchase } from './idb'
import { offlineQueue, TransactionType } from './offlineQueue'

// Use environment variable for API URL, fallback to relative path for local dev
const API_BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api`
  : '/api'

// Configure offline queue with API settings
offlineQueue.configure({
  apiBaseUrl: API_BASE,
  getHeaders: getHeaders
})

// Default timeout for fetch requests (ms)
const DEFAULT_TIMEOUT = 15000
const SYNC_TIMEOUT = 30000

// In-flight write counter — used by the SW update handler in main.tsx
// to defer page reload while a sale/payment/etc. is still posting. Without
// this, a controllerchange fires mid-POST and the page reloads, aborting
// the request and silently losing the transaction.
let inFlightWrites = 0
export function getInFlightWrites(): number { return inFlightWrites }
function trackWrite<T>(fn: () => Promise<T>): Promise<T> {
  inFlightWrites++
  return fn().finally(() => {
    inFlightWrites = Math.max(0, inFlightWrites - 1)
    if (inFlightWrites === 0 && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('api:idle'))
    }
  })
}

/**
 * Fetch with an abort-controller timeout to prevent hanging requests.
 * Uses 15s default; pass SYNC_TIMEOUT (30s) for sync operations.
 */
async function fetchWithTimeout(url: string, options: RequestInit, timeoutMs = DEFAULT_TIMEOUT): Promise<Response> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const response = await fetch(url, { ...options, signal: controller.signal })
    return response
  } finally {
    clearTimeout(timeoutId)
  }
}

function getHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  }

  const token = localStorage.getItem('token')
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  return headers
}

// Fired when the server reports 401. AuthContext listens and logs the user out.
function notifyAuthExpired(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('auth:expired'))
  }
}

async function handleResponse<T>(response: Response): Promise<ApiResult<T>> {
  if (response.status === 401) {
    // A 401 means one of two very different things:
    //   (a) a REJECTED LOGIN (wrong credentials) — the user never had a
    //       session, so we must NOT fire auth:expired or force a logout;
    //       surface it as a plain invalid-credentials error on the form.
    //   (b) a real EXPIRED/REVOKED session on an authenticated request —
    //       fire auth:expired so AuthContext logs out cleanly.
    // The server tags (a) with error.code === 'INVALID_CREDENTIALS'.
    let code: string | undefined
    try {
      const body = await response.json()
      code = body?.error?.code
    } catch {
      // Non-JSON 401 (e.g. edge error) — fall through to session-expiry.
    }

    if (code === 'INVALID_CREDENTIALS') {
      return {
        success: false,
        error: { code, message: 'Invalid mobile number, email, or password.' }
      }
    }

    notifyAuthExpired()
    return {
      success: false,
      error: { code: 'UNAUTHORIZED', message: 'Your session has expired. Please log in again.' }
    }
  }

  // Verify the response is JSON before attempting to parse
  const contentType = response.headers.get('content-type')
  if (!contentType || !contentType.includes('application/json')) {
    return {
      success: false,
      error: {
        code: 'INVALID_RESPONSE',
        message: `Server returned non-JSON response (${response.status})`
      }
    }
  }

  try {
    const data = await response.json()

    if (!response.ok && data.success !== false) {
      // Worker always returns { success: false, error: { code, message } } on
      // failures, but if a 4xx/5xx slips through without that envelope (e.g.
      // unhandled Hono error, Cloudflare edge error), prefer the server's
      // error.message when it IS present rather than masking it with a
      // generic "HTTP 400" string the user can't act on.
      return {
        success: false,
        error: {
          code: data?.error?.code || 'HTTP_ERROR',
          message: data?.error?.message || `HTTP ${response.status}: ${response.statusText}`
        }
      }
    }

    return data as ApiResult<T>
  } catch {
    return {
      success: false,
      error: { code: 'PARSE_ERROR', message: 'Failed to parse server response' }
    }
  }
}

// Map API paths to cache stores
const CACHE_STORE_MAP: Record<string, typeof STORES[keyof typeof STORES]> = {
  '/customers': STORES.CUSTOMERS,
  '/products': STORES.PRODUCTS,
  // The sale and purchase screens read this one, NOT /products — without
  // it the product list was empty offline, so a driver with no coverage
  // could not sell anything. Matched exactly, so it never collides with
  // '/products' above.
  '/products/with-uoms': STORES.PRODUCTS_WITH_UOMS,
  '/suppliers': STORES.SUPPLIERS,
  '/inventory/van': STORES.INVENTORY
}

// Map API paths to transaction types
const TRANSACTION_TYPE_MAP: Record<string, TransactionType> = {
  '/sales': 'sale',
  '/purchases': 'purchase',
  '/payments': 'payment',
  '/inventory/adjust': 'inventory_adjust'
}

/**
 * Try to get cached data for a path
 */
async function getCachedResponse<T>(path: string, ignoreAge = false): Promise<T[] | null> {
  const store = CACHE_STORE_MAP[path]
  if (!store) return null

  try {
    // Online, 5 minutes keeps things fresh. OFFLINE, age is not a reason to
    // withhold: stale data is the ONLY data, and refusing to show it does
    // not make it fresher — it just empties the screen. This used to expire
    // at 60 minutes, so an hour into a day with no coverage the customer
    // and product lists simply vanished and the driver could sell nothing.
    // The age is surfaced in the offline banner instead, so they can judge
    // it themselves.
    // `ignoreAge` is set when we are falling back after a request FAILED.
    // navigator.onLine only means "attached to a network", not "the internet
    // answers" — a van with one bar, or a captive-portal wifi, reports
    // online while every request dies. Applying the 5-minute rule there
    // refused cached data for being six minutes old and put "Failed to
    // fetch" on screen with a perfectly good copy sitting in IndexedDB.
    const maxAge = ignoreAge || !navigator.onLine ? Number.POSITIVE_INFINITY : 5
    return await idb.getCachedData<T>(store, maxAge)
  } catch (error) {
    console.warn('[API] Cache read failed:', error)
    return null
  }
}

/**
 * Cache response data
 */
async function cacheResponse<T>(path: string, data: T[]): Promise<void> {
  const store = CACHE_STORE_MAP[path]
  if (!store) return

  try {
    await idb.cacheData(store, data)
  } catch (error) {
    console.warn('[API] Cache write failed:', error)
  }
}

/**
 * Check if a path should be cached
 */
function isCacheablePath(path: string): boolean {
  return path in CACHE_STORE_MAP
}

/**
 * Check if a path is for a queueable transaction.
 *
 * EXACT match only. startsWith() also captured sub-resource actions like
 * /sales/:id/cancel and /payments/:id/cancel — while offline those were
 * silently queued as brand-new "sale"/"payment" transactions, bypassing
 * every server-side cancel guard and corrupting the queue on sync.
 * Cancellations require the server's validation and must fail offline.
 */
function isQueueablePath(path: string): boolean {
  return path in TRANSACTION_TYPE_MAP
}

/**
 * Get transaction type for a path
 */
function getTransactionType(path: string): TransactionType | null {
  return TRANSACTION_TYPE_MAP[path] ?? null
}

// Revalidation callbacks for stale-while-revalidate pattern
/**
 * Who wants to know when a cached list is refreshed.
 *
 * A SET per path, not a single callback. It used to be one: the second
 * screen to subscribe silently replaced the first, and nothing ever
 * unsubscribed — so a callback from a screen the user left ten minutes ago
 * was still registered, writing into state that no longer existed, while
 * the screen actually on display got nothing.
 */
const revalidateCallbacks: Map<string, Set<(data: unknown) => void>> = new Map()

export const api = {
  /**
   * Register a callback to be notified when background revalidation completes
   * for a given path (stale-while-revalidate pattern).
   */
  onRevalidate(path: string, callback: (data: unknown) => void): () => void {
    let subscribers = revalidateCallbacks.get(path)
    if (!subscribers) {
      subscribers = new Set()
      revalidateCallbacks.set(path, subscribers)
    }
    subscribers.add(callback)
    // Returns an unsubscribe, so a screen can let go on unmount. Without
    // one, every screen ever visited stays subscribed for the session.
    return () => {
      subscribers!.delete(callback)
      if (subscribers!.size === 0) revalidateCallbacks.delete(path)
    }
  },

  async get<T>(path: string): Promise<ApiResult<T>> {
    // Try cache first for cacheable paths
    if (isCacheablePath(path)) {
      const cached = await getCachedResponse<T>(path)
      if (cached !== null) {
        // Return cached data immediately
        // If online, fetch fresh data in background
        if (navigator.onLine) {
          this.fetchAndCache<T>(path).catch(() => {})
        }
        return { success: true, data: cached as T }
      }
    }

    // If offline and no cache, return error
    if (!navigator.onLine) {
      return {
        success: false,
        error: {
          code: 'OFFLINE',
          message: 'You are offline and no cached data is available'
        }
      }
    }

    // Fetch from network
    try {
      const response = await fetchWithTimeout(`${API_BASE}${path}`, {
        method: 'GET',
        headers: getHeaders()
      })
      const result = await handleResponse<T>(response)

      // Cache successful responses for cacheable paths
      if (result.success && isCacheablePath(path) && Array.isArray(result.data)) {
        await cacheResponse(path, result.data)
      }

      return result
    } catch (error) {
      // Network error - try cache as fallback, at ANY age: the request
      // just failed, so old data beats no data.
      if (isCacheablePath(path)) {
        const cached = await getCachedResponse<T>(path, true)
        if (cached !== null) {
          return { success: true, data: cached as T }
        }
      }

      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network request failed'
        }
      }
    }
  },

  /**
   * Correct ONE product's cached van quantity, immediately.
   *
   * The sale and purchase screens read stock from the cached
   * /products/with-uoms rows, not from /inventory/van. Stale-while-
   * revalidate means the first visit after an adjustment is served the OLD
   * number and only the SECOND is right — which is exactly the "I adjusted
   * it and New Sale still shows the old stock" report.
   *
   * Waiting for the background refresh is not enough on its own: the van
   * may have no signal, and a driver who has just counted his stock needs
   * the number he counted, not the one from this morning.
   */
  async patchCachedVanQuantity(productId: string, newQuantity: number): Promise<void> {
    try {
      const rows = await idb.getAll<{ product_id: string; van_quantity: number }>(STORES.PRODUCTS_WITH_UOMS)
      const row = rows.find(r => r.product_id === productId)
      if (!row) return
      await idb.put(STORES.PRODUCTS_WITH_UOMS, { ...row, van_quantity: newQuantity })
      const patched = rows.map(r => (r.product_id === productId ? { ...r, van_quantity: newQuantity } : r))
      revalidateCallbacks.get('/products/with-uoms')?.forEach(cb => cb(patched))
    } catch { /* cache unavailable — the next refresh will correct it */ }
  },

  /**
   * Fetch and cache data in background (stale-while-revalidate)
   */
  async fetchAndCache<T>(path: string): Promise<ApiResult<T> | null> {
    try {
      const response = await fetchWithTimeout(`${API_BASE}${path}`, {
        method: 'GET',
        headers: getHeaders()
      })
      const result = await handleResponse<T>(response)

      if (result.success && Array.isArray(result.data)) {
        await cacheResponse(path, result.data)
        // Notify every subscriber, not just the most recent one.
        revalidateCallbacks.get(path)?.forEach(cb => cb(result.data))
      }
      // Returned for getFresh. Background callers ignore it.
      return result
    } catch {
      // Silent fail for background refresh
      return null
    }
  },

  /**
   * A GET that must see the server rather than the 5-minute cache.
   *
   * `get` on a cacheable path answers from IndexedDB and revalidates in the
   * background. That is right for opening a screen and wrong straight after
   * a write, because a caller refetching at that moment is asking precisely
   * because it just changed something. The purchase screen reloaded the
   * catalogue after a product edit and was handed the costs from before the
   * edit, so the cart line kept the old figure while the product page
   * showed the new one.
   *
   * Offline, or when the request fails, it falls back to `get` — so the
   * caller is never worse off than it would have been.
   */
  async getFresh<T>(path: string): Promise<ApiResult<T>> {
    if (!navigator.onLine) return this.get<T>(path)
    const result = await this.fetchAndCache<T>(path)
    return result ?? this.get<T>(path)
  },

  async post<T>(path: string, body?: unknown, explicitIdempotencyKey?: string): Promise<ApiResult<T>> {
    return trackWrite(async () => {
    // If offline and this is a queueable transaction, add to queue
    if (!navigator.onLine && isQueueablePath(path)) {
      const txType = getTransactionType(path)
      if (txType) {
        // Update local inventory for offline transactions
        const data = body as Record<string, unknown>
        if (txType === 'sale' && data?.items && data.source !== 'warehouse') {
          // A warehouse sale did not come off this van, so the local cache
          // must not be reduced — doing so would make the driver's own
          // stock read short and then block his next sale.
          // Preserve conversion_factor — IDB stores inventory in base units
          // and stockQtyFor multiplies quantity * conversion_factor. Stripping
          // the factor here (which we used to do) made every multi-UOM
          // offline sale drift IDB from the server's base-unit count.
          const items = data.items as Array<{ product_id: string; quantity: number; conversion_factor?: number }>
          const success = await deductInventoryForSale(items)
          if (!success) {
            return {
              success: false,
              error: {
                code: 'INSUFFICIENT_INVENTORY',
                message: 'Not enough inventory for this sale'
              }
            }
          }
        } else if (txType === 'purchase' && data?.items && data.destination !== 'warehouse') {
          // A warehouse purchase must NOT credit the local van cache: the van view
          // would show stock that is not on board, and the oversell guard would
          // then let the driver sell it. The server puts it in the warehouse;
          // the cache only ever mirrors this van.
          const items = data.items as Array<{ product_id: string; quantity: number; conversion_factor?: number }>
          // Purchases are stored in base units server-side too; reconcile by
          // multiplying through conversion_factor before crediting IDB.
          const baseUnitItems = items.map(i => ({
            ...i,
            quantity: i.quantity * (i.conversion_factor || 1),
          }))
          await addInventoryForPurchase(baseUnitItems)
        }

        const tx = await offlineQueue.queue(txType, path, 'POST', body)
        return {
          success: true,
          data: {
            _offline: true,
            _localId: tx.local_id,
            message: 'Transaction queued for sync'
          } as T
        }
      }
    }

    // If offline and not queueable, return error
    if (!navigator.onLine) {
      return {
        success: false,
        error: {
          code: 'OFFLINE',
          message: 'Cannot perform this action while offline'
        }
      }
    }

    // Mint an idempotency key for queueable paths BEFORE the network
    // attempt. The worker's withIdempotency middleware deduplicates
    // against this key, so if the request lands twice (network drop after
    // server commit; user retries; we fall back to the offline queue and
    // sync) the worker returns the cached response instead of creating
    // a second sale/purchase/payment.
    // A caller can supply a STABLE key (reused across manual retries of the
    // same logical submit) so a create that committed server-side but whose
    // response was lost is deduped instead of duplicated — even for paths
    // that aren't offline-queueable (e.g. quotations). Queueable paths still
    // auto-mint a key for the offline-sync retry path.
    const queueable = isQueueablePath(path)
    const idempotencyKey = explicitIdempotencyKey ?? (queueable ? crypto.randomUUID() : undefined)
    const postHeaders = idempotencyKey
      ? { ...getHeaders(), 'Idempotency-Key': idempotencyKey }
      : getHeaders()

    try {
      const response = await fetchWithTimeout(`${API_BASE}${path}`, {
        method: 'POST',
        headers: postHeaders,
        body: body ? JSON.stringify(body) : undefined
      })
      return handleResponse<T>(response)
    } catch (error) {
      // Network error during POST - try to queue if queueable. Reuse the
      // SAME idempotency key so the eventual sync request matches any
      // server-side row that the failed request may have created before
      // the connection dropped.
      if (queueable) {
        const txType = getTransactionType(path)
        if (txType) {
          // Update local inventory for offline transactions
          const data = body as Record<string, unknown>
          if (txType === 'sale' && data?.items && data.source !== 'warehouse') {
            // See above: warehouse stock never left this van's cache.
            const items = data.items as Array<{ product_id: string; quantity: number }>
            const success = await deductInventoryForSale(items)
            if (!success) {
              return {
                success: false,
                error: {
                  code: 'INSUFFICIENT_INVENTORY',
                  message: 'Not enough inventory for this sale'
                }
              }
            }
          } else if (txType === 'purchase' && data?.items && data.destination !== 'warehouse') {
            // See above: warehouse stock never enters this van's cache.
            const items = data.items as Array<{ product_id: string; quantity: number }>
            await addInventoryForPurchase(items)
          }

          // Queueing is the ONLY thing standing between the driver and a
          // lost sale, and it can fail: IndexedDB is unavailable in private
          // mode, over quota, or blocked mid-upgrade by another tab. It sits
          // inside a catch, so an unguarded throw escaped api.post entirely
          // — AFTER the local stock had already been deducted above. The
          // sale vanished and the van count was left wrong.
          try {
            const tx = await offlineQueue.queue(txType, path, 'POST', body, idempotencyKey)
            return {
              success: true,
              data: {
                _offline: true,
                _localId: tx.local_id,
                message: 'Transaction queued for sync'
              } as T
            }
          } catch (queueError) {
            // Put the stock back: the deduction above assumed a queued sale
            // that now does not exist. Leaving it would silently understate
            // the van for the rest of the day.
            if (txType === 'sale' && data?.items) {
              await restoreInventoryForFailedSale(
                data.items as Array<{ product_id: string; quantity: number }>
              ).catch(() => { /* best effort — already in a failure path */ })
            } else if (txType === 'purchase' && data?.items) {
              await restoreInventoryForFailedPurchase(
                data.items as Array<{ product_id: string; quantity: number }>
              ).catch(() => { /* best effort */ })
            }
            return {
              success: false,
              error: {
                code: 'QUEUE_FAILED',
                message: 'Could not save this offline. Nothing was recorded — please try again.' +
                  (queueError instanceof Error ? ` (${queueError.message})` : '')
              }
            }
          }
        }
      }

      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network request failed'
        }
      }
    }
    })
  },

  async put<T>(path: string, body?: unknown): Promise<ApiResult<T>> {
    return trackWrite(async () => {
    // PUT requests are not queued - they require immediate response
    if (!navigator.onLine) {
      return {
        success: false,
        error: {
          code: 'OFFLINE',
          message: 'Cannot update while offline'
        }
      }
    }

    try {
      const response = await fetchWithTimeout(`${API_BASE}${path}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: body ? JSON.stringify(body) : undefined
      })
      return handleResponse<T>(response)
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network request failed'
        }
      }
    }
    })
  },

  async delete<T>(path: string): Promise<ApiResult<T>> {
    return trackWrite(async () => {
    // DELETE requests are not queued
    if (!navigator.onLine) {
      return {
        success: false,
        error: {
          code: 'OFFLINE',
          message: 'Cannot delete while offline'
        }
      }
    }

    try {
      const response = await fetchWithTimeout(`${API_BASE}${path}`, {
        method: 'DELETE',
        headers: getHeaders()
      })
      return handleResponse<T>(response)
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network request failed'
        }
      }
    }
    })
  },

  /**
   * Force refresh cache for a path
   */
  async refreshCache(path: string): Promise<void> {
    if (!navigator.onLine) return
    await this.fetchAndCache(path)
  },

  /**
   * Clear all cached data — including any queued offline transactions.
   * Callers shouldn't need a second step to fully reset.
   */
  async clearCache(): Promise<void> {
    await idb.clear(STORES.CUSTOMERS)
    await idb.clear(STORES.PRODUCTS)
    await idb.clear(STORES.SUPPLIERS)
    await idb.clear(STORES.INVENTORY)
    await idb.clear(STORES.CACHE_META)
    await idb.clear(STORES.PENDING_TRANSACTIONS)
  },

  /**
   * Pre-cache essential data for offline use
   */
  async preCacheData(): Promise<void> {
    if (!navigator.onLine) return

    const paths = Object.keys(CACHE_STORE_MAP)
    await Promise.all(paths.map(path => this.fetchAndCache(path)))
  }
}

// Export timeout constants for use in sync operations
export { SYNC_TIMEOUT }
